import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { TransportService } from 'service/TransportService';
import { FreightsService } from 'service/FreightsService';
import ExchangeTransportSearch from 'models/exchangeTransportSearch';
import FreightSearchModel from 'models/freightSearch';
import { socket } from 'helpers/socket';

import { TemplateData, MapItem, TemplateMapVariant, TransportItem, FreightItem } from '../types';
import { formatDateForInput } from '../utils';

interface RootState {
  settings: { lang: string };
}

interface UseTemplateMapDataProps {
  templateId?: string;
  variant: TemplateMapVariant;
}

interface UseTemplateMapDataReturn {
  template: TemplateData | null;
  items: MapItem[];
  count: number;
  isLoading: boolean;
  error: string | null;
  loadingRadius: number;
  unloadingRadius: number;
  dateFrom: string;
  dateTo: string;
  isRefetching: boolean;
  newItemsCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  setLoadingRadius: (value: number) => void;
  setUnloadingRadius: (value: number) => void;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  handleApplyFilters: () => Promise<void>;
  handleRefreshNewItems: () => Promise<void>;
  handleLoadMore: () => Promise<void>;
}

export const useTemplateMapData = ({
  templateId,
  variant,
}: UseTemplateMapDataProps): UseTemplateMapDataReturn => {
  const { t } = useTranslation();
  const lang = useSelector((state: RootState) => state.settings.lang);

  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [items, setItems] = useState<MapItem[]>([]);
  const [count, setCount] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingRadius, setLoadingRadius] = useState<number>(0);
  const [unloadingRadius, setUnloadingRadius] = useState<number>(0);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [isRefetching, setIsRefetching] = useState(false);
  const [newItemsCount, setNewItemsCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Cursor for next page
  const nextCursorRef = useRef<string | undefined>(undefined);

  // Track how many chunks have been loaded (for lazy loading gating)
  const chunkCountRef = useRef(0);

  // Prefetched next chunk - always have +1 chunk ready
  const prefetchedRef = useRef<{ items: MapItem[]; hasMore: boolean; nextCursor?: string } | null>(null);
  const isPrefetchingRef = useRef(false);

  const PAGE_SIZE = 20;

  const fetchPage = async (
    templateData: TemplateData,
    loadRad: number,
    unloadRad: number,
    cursor?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<{ items: MapItem[]; hasMore: boolean; count: number; nextCursor?: string }> => {
    let fetchedItems: MapItem[] = [];
    let fetchedCount: number;
    let fetchedNextCursor: string | undefined;

    if (variant === 'transport') {
      const searchData = ExchangeTransportSearch.searchData({
        ...templateData,
        loading_address_radius: loadRad,
        unloading_address_radius: unloadRad,
        available_from: fromDate || templateData.available_from,
        available_to: toDate || templateData.available_to,
      });

      const response = await TransportService.searchExchange(
        { lang, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        { ...searchData }
      );
      fetchedItems = response.data.items || [];
      fetchedCount = response.data.count;
      fetchedNextCursor = response.data.nextCursor;
    } else {
      const searchData = FreightSearchModel.searchData({
        ...templateData,
        loading_address_radius: loadRad,
        unloading_address_radius: unloadRad,
        loading_from: fromDate || templateData.loading_from,
        loading_to: toDate || templateData.loading_to,
      });

      const response = await FreightsService.searchCargo(
        { lang },
        { ...searchData, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }
      );
      fetchedItems = response.data.items || [];
      fetchedCount = response.data.count;
      fetchedNextCursor = response.data.nextCursor;
    }

    return {
      count: fetchedCount,
      items: fetchedItems,
      hasMore: !!fetchedNextCursor,
      nextCursor: fetchedNextCursor,
    };
  };

  // Prefetch next chunk in background
  const prefetchNextChunk = useCallback(async (
    templateData: TemplateData,
    loadRad: number,
    unloadRad: number,
    cursor: string,
    fromDate?: string,
    toDate?: string
  ) => {
    if (isPrefetchingRef.current) return;
    isPrefetchingRef.current = true;

    try {
      const result = await fetchPage(templateData, loadRad, unloadRad, cursor, fromDate, toDate);
      prefetchedRef.current = { items: result.items, hasMore: result.hasMore, nextCursor: result.nextCursor };
    } catch (err) {
      console.error('Error prefetching:', err);
      prefetchedRef.current = null;
    } finally {
      isPrefetchingRef.current = false;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!templateId) return;

    const fetchData = async () => {
      setIsLoading(true);
      chunkCountRef.current = 0;
      try {
        let templateData: TemplateData;

        if (variant === 'transport') {
          const response = await TransportService.getExchangePattern(templateId, lang);
          templateData = response.data.data;
        } else {
          const response = await FreightsService.getCargoPattern(templateId, lang);
          templateData = response.data.data;
        }

        setTemplate(templateData);
        setLoadingRadius(templateData.loading_address_radius || 0);
        setUnloadingRadius(templateData.unloading_address_radius || 0);

        const initialDateFrom = formatDateForInput(
          variant === 'transport' ? templateData.available_from : templateData.loading_from
        );
        const initialDateTo = formatDateForInput(
          variant === 'transport' ? templateData.available_to : templateData.loading_to
        );
        setDateFrom(initialDateFrom);
        setDateTo(initialDateTo);

        const result = await fetchPage(
          templateData,
          templateData.loading_address_radius || 0,
          templateData.unloading_address_radius || 0,
          undefined,
          initialDateFrom,
          initialDateTo
        );
        setItems(result.items);
        setCount(result.count);
        setHasMore(result.hasMore);
        nextCursorRef.current = result.nextCursor;
        chunkCountRef.current = 1;

        // Prefetch next chunk if there's more data
        if (result.hasMore && result.nextCursor) {
          prefetchNextChunk(
            templateData,
            templateData.loading_address_radius || 0,
            templateData.unloading_address_radius || 0,
            result.nextCursor,
            initialDateFrom,
            initialDateTo
          );
        }
      } catch (err) {
        console.error('TemplateMap: Error loading data:', err);
        setError(t('errors.loadFailed', 'Ошибка загрузки'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [templateId, lang, variant, t]);

  // WebSocket listener
  useEffect(() => {
    if (!templateId) return;

    const handleNewItemAvailable = (data: { templateIds?: string[]; template_id?: string; count?: number }) => {
      const ids = data?.templateIds || (data?.template_id ? [data.template_id] : []);
      const itemCount = data?.count || 1;

      if (ids.includes(templateId)) {
        setNewItemsCount(prev => prev + itemCount);
      }
    };

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const setupListener = () => {
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.on('search_templates.new_item_available', handleNewItemAvailable);
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return true;
      }
      return false;
    };

    if (!setupListener()) {
      intervalId = setInterval(() => setupListener(), 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.off('search_templates.new_item_available', handleNewItemAvailable);
      }
    };
  }, [templateId]);

  const handleApplyFilters = async () => {
    if (!template || !templateId) return;
    setIsRefetching(true);
    setNewItemsCount(0);
    chunkCountRef.current = 0;
    prefetchedRef.current = null; // Clear any prefetched data

    try {
      const result = await fetchPage(
        template,
        loadingRadius,
        unloadingRadius,
        undefined,
        dateFrom || undefined,
        dateTo || undefined
      );
      setItems(result.items);
      setHasMore(result.hasMore);
      nextCursorRef.current = result.nextCursor;
      chunkCountRef.current = 1;

      // Prefetch next chunk if there's more
      if (result.hasMore && result.nextCursor) {
        prefetchNextChunk(
          template,
          loadingRadius,
          unloadingRadius,
          result.nextCursor,
          dateFrom || undefined,
          dateTo || undefined
        );
      }

      // Update template on server
      if (variant === 'transport') {
        const formData = ExchangeTransportSearch.formData(template);
        const updateData: any = {
          ...formData,
          loading_address_radius: loadingRadius,
          unloading_address_radius: unloadingRadius,
          available_from: dateFrom || template.available_from,
          available_to: dateTo || template.available_to,
        };
        const patchData = ExchangeTransportSearch.templateData(updateData);
        await TransportService.updateExchangePattern(templateId, patchData);
      } else {
        const formData = FreightSearchModel.formData(template);
        const updateData: any = {
          ...formData,
          loading_address_radius: loadingRadius,
          unloading_address_radius: unloadingRadius,
          loading_from: dateFrom || formData.loading_from,
          loading_to: dateTo || formData.loading_to,
        };
        const patchData = FreightSearchModel.updateTemplateData(updateData);
        await FreightsService.updateCargoPattern(templateId, patchData);
      }
    } catch (err) {
      console.error('Error applying filters:', err);
    } finally {
      setIsRefetching(false);
    }
  };

  const handleRefreshNewItems = useCallback(async () => {
    if (!template) return;
    setIsRefetching(true);
    setNewItemsCount(0);
    chunkCountRef.current = 0;
    prefetchedRef.current = null; // Clear any prefetched data

    try {
      const result = await fetchPage(
        template,
        loadingRadius,
        unloadingRadius,
        undefined,
        dateFrom || undefined,
        dateTo || undefined
      );
      setItems(result.items);
      setHasMore(result.hasMore);
      nextCursorRef.current = result.nextCursor;
      chunkCountRef.current = 1;

      // Prefetch next chunk if there's more
      if (result.hasMore && result.nextCursor) {
        prefetchNextChunk(
          template,
          loadingRadius,
          unloadingRadius,
          result.nextCursor,
          dateFrom || undefined,
          dateTo || undefined
        );
      }
    } catch (err) {
      console.error('Error refreshing:', err);
    } finally {
      setIsRefetching(false);
    }
  }, [template, loadingRadius, unloadingRadius, dateFrom, dateTo, prefetchNextChunk]);

  const handleLoadMore = useCallback(async () => {
    if (!template || !hasMore || isLoadingMore) return;

    // Use prefetched data if available (instant render)
    if (prefetchedRef.current) {
      const prefetched = prefetchedRef.current;
      prefetchedRef.current = null;

      setItems(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const newItems = prefetched.items.filter(item => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
      setHasMore(prefetched.hasMore);
      nextCursorRef.current = prefetched.nextCursor;
      chunkCountRef.current += 1;

      // Prefetch next chunk if there's more
      if (prefetched.hasMore && prefetched.nextCursor) {
        prefetchNextChunk(
          template,
          loadingRadius,
          unloadingRadius,
          prefetched.nextCursor,
          dateFrom || undefined,
          dateTo || undefined
        );
      }
      return;
    }

    // Fallback: fetch if no prefetched data available
    if (!nextCursorRef.current) return;
    setIsLoadingMore(true);
    try {
      const result = await fetchPage(
        template,
        loadingRadius,
        unloadingRadius,
        nextCursorRef.current,
        dateFrom || undefined,
        dateTo || undefined
      );
      setItems(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const newItems = result.items.filter(item => !existingIds.has(item.id));
        return [...prev, ...newItems];
      });
      setHasMore(result.hasMore);
      nextCursorRef.current = result.nextCursor;
      chunkCountRef.current += 1;

      // Prefetch next chunk if there's more
      if (result.hasMore && result.nextCursor) {
        prefetchNextChunk(
          template,
          loadingRadius,
          unloadingRadius,
          result.nextCursor,
          dateFrom || undefined,
          dateTo || undefined
        );
      }
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [template, loadingRadius, unloadingRadius, dateFrom, dateTo, hasMore, isLoadingMore, prefetchNextChunk]);

  return {
    template,
    items,
    count,
    isLoading,
    error,
    loadingRadius,
    unloadingRadius,
    dateFrom,
    dateTo,
    isRefetching,
    newItemsCount,
    hasMore,
    isLoadingMore,
    setLoadingRadius,
    setUnloadingRadius,
    setDateFrom,
    setDateTo,
    handleApplyFilters,
    handleRefreshNewItems,
    handleLoadMore,
  };
};
